import Cookies from 'js-cookie';

/**
 * Where the session tokens live. The web keeps them in cookies; the native app swaps in a secure store (F3a).
 * Every read and write of the access/refresh token goes through here (guarded by token-cookie-sites.test.ts).
 */
export interface TokenStore {
  ready(): Promise<void>;
  getAccess(): string | null;
  getRefresh(): string | null;
  set(access: string, refresh: string): void | Promise<void>;
  clear(): void | Promise<void>;
}

/** The web store, with the console's extra writes: a platform session is an access token alone, so it carries its own lifetime. */
export interface CookieTokenStore extends TokenStore {
  /** Replace the access token and drop the refresh token (console sessions and impersonation never refresh). */
  setAccessOnly(access: string, expiresDays: number): void;
}

export const secureCookie = () => ({ sameSite: 'lax' as const, secure: import.meta.env.PROD, path: '/' });

export const cookieTokenStore: CookieTokenStore = {
  ready: () => Promise.resolve(),
  getAccess: () => Cookies.get('access_token') || null,
  getRefresh: () => Cookies.get('refresh_token') || null,
  set(access, refresh) {
    // SameSite=lax for CSRF protection; path '/' so the cookie is available across all routes
    const options = { expires: 1, ...secureCookie() };
    Cookies.set('access_token', access, options);
    Cookies.set('refresh_token', refresh, { ...options, expires: 30 }); // the refresh token lives 30 days on the server
  },
  clear() {
    Cookies.remove('access_token', { path: '/' });
    Cookies.remove('refresh_token', { path: '/' });
  },
  setAccessOnly(access, expiresDays) {
    Cookies.set('access_token', access, { ...secureCookie(), expires: expiresDays });
    Cookies.remove('refresh_token', { path: '/' });
  },
};

/** Both tokens in memory only: the app's store until its native store is in place (never cookies, which a WebView persists). */
export function createMemoryTokenStore(): TokenStore {
  let access: string | null = null;
  let refresh: string | null = null;
  return {
    ready: () => Promise.resolve(),
    getAccess: () => access,
    getRefresh: () => refresh,
    set(a, r) {
      access = a;
      refresh = r;
    },
    clear() {
      access = null;
      refresh = null;
    },
  };
}

/** Replaced by the native store at startup in the app build (F3a). */
export let tokenStore: TokenStore = cookieTokenStore;

export function setTokenStore(store: TokenStore): void {
  tokenStore = store;
}
